import React, { useState, useRef, useCallback, useId } from 'react';
import { motion } from 'motion/react';
import { UploadSimple, X } from '@phosphor-icons/react';
import api from '../services/api';
import { useScrollLock } from '../hooks/useScrollLock';
import { MAX_UPLOAD_IMAGE_BYTES } from '../utils/uploadLimits';
import Modal from './Modal';

const PANEL_MOTION = {
  initial: { opacity: 0, scale: 0.95, y: 20 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 20 },
};

export default function UploadModal({ onClose, onUpload }) {
  const titleId = useId();
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Lock body scroll when modal is open
  const { targetRef: modalRef, unlock: unlockScroll } = useScrollLock(true);

  // Wrap onClose to unlock scroll before exit animation
  const handleClose = useCallback(() => {
    unlockScroll();
    onClose();
  }, [onClose, unlockScroll]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      selectFile(droppedFile);
    } else {
      setError('Please drop an image file');
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      selectFile(selectedFile);
    }
  };

  const selectFile = (f) => {
    // Same guard as paste-to-upload (COL-96) — the same file should be
    // rejected the same way regardless of how it entered the app.
    if (f.size > MAX_UPLOAD_IMAGE_BYTES) {
      setError(`"${f.name || 'This image'}" is too large to upload (max 20MB)`);
      return;
    }

    setFile(f);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      // Simulate progress (actual upload doesn't report progress easily)
      const progressInterval = setInterval(() => {
        setProgress((p) => Math.min(p + 10, 90));
      }, 100);

      const { item, jobId } = await api.uploadFile(file);

      clearInterval(progressInterval);
      setProgress(100);

      setTimeout(() => {
        onUpload(item, jobId);
      }, 300);
    } catch (err) {
      setError(err.message || 'Upload failed');
      setUploading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Modal
      onClose={handleClose}
      labelledBy={titleId}
      // Named (rather than "") so the safe-area rule below (COL-90) can
      // live in index.css per the project's no-CSS-in-JS convention,
      // instead of adding another inline-style literal here.
      className="upload-modal"
      panelRef={modalRef}
      panelMotionProps={PANEL_MOTION}
      panelStyle={{
        background: 'var(--surface-raised)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-2xl)',
        maxWidth: '500px',
        width: '90%',
        boxShadow: 'var(--shadow-xl)',
      }}
    >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-xl)',
        }}>
          <h2 id={titleId} style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem',
          }}>
            Upload Screenshot
          </h2>
          <button
            onClick={handleClose}
            style={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-muted)',
              cursor: 'pointer',
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {!file ? (
          <div
            className={`upload-dropzone ${isDragging ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="upload-dropzone-icon">
              <UploadIcon />
            </div>
            <p className="upload-dropzone-text">
              Drop an image here, or click to browse
            </p>
            <p className="upload-dropzone-hint">
              Supports PNG, JPG, GIF, WebP
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>
        ) : (
          <div>
            <div style={{
              position: 'relative',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden',
              marginBottom: 'var(--space-lg)',
            }}>
              <img
                src={preview}
                alt="Preview"
                style={{
                  width: '100%',
                  maxHeight: '300px',
                  objectFit: 'contain',
                  background: 'var(--surface-sunken)',
                }}
              />
              {!uploading && (
                <button
                  onClick={clearFile}
                  style={{
                    position: 'absolute',
                    top: 'var(--space-sm)',
                    right: 'var(--space-sm)',
                    width: 32,
                    height: 32,
                    background: 'var(--scrim-strong)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-inverse)',
                    cursor: 'pointer',
                  }}
                >
                  <CloseIcon size={16} />
                </button>
              )}
            </div>

            <div style={{
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
              marginBottom: 'var(--space-md)',
            }}>
              {file.name}
              <span style={{ color: 'var(--text-muted)', marginLeft: 'var(--space-sm)' }}>
                ({formatFileSize(file.size)})
              </span>
            </div>

            {uploading && (
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <div style={{
                  height: 4,
                  background: 'var(--border)',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}>
                  <motion.div
                    style={{
                      height: '100%',
                      background: 'var(--accent)',
                    }}
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                  />
                </div>
                <p style={{
                  fontSize: '0.8125rem',
                  color: 'var(--text-muted)',
                  marginTop: 'var(--space-sm)',
                  textAlign: 'center',
                }}>
                  {progress < 100 ? 'Uploading...' : 'Complete!'}
                </p>
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{
            background: 'var(--danger-soft)',
            color: 'var(--danger)',
            padding: 'var(--space-md)',
            borderRadius: 'var(--radius-md)',
            marginTop: 'var(--space-md)',
            fontSize: '0.875rem',
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--space-md)',
          marginTop: 'var(--space-xl)',
        }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={uploading}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload & Analyze'}
          </button>
        </div>
    </Modal>
  );
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function UploadIcon() {
  return <UploadSimple size={64} />;
}

function CloseIcon({ size = 20 }) {
  return <X size={size} />;
}
